import { SiteInfo } from '@sitecore-content-sdk/nextjs';
import domains from 'scripts/domains/domains.json';
import { SiteDomainLocale } from 'lib/site-domain';
import { mergedSites } from 'lib/site-resolver/build-sites';

const LEADING_DUPLICATED_LOCALE_PREFIX_PATTERN = /^\/([a-z]{2}-[a-z]{2})(?:\/\1)*(?=\/|$)/i;

function normalizeSiteDomain(siteDomain: SiteDomainLocale): SiteDomainLocale {
  return {
    ...siteDomain,
    domain: siteDomain.domain?.trim(),
    previewDomain: siteDomain.previewDomain?.trim(),
    siteGroupingHostName: siteDomain.siteGroupingHostName?.trim(),
    defaultLocale: siteDomain.defaultLocale?.trim(),
    locales: siteDomain.locales?.map((value) => value.trim()) ?? [],
  };
}

export function getConfiguredSiteDomains(): SiteDomainLocale[] {
  const configuredDomains = process.env.SITE_DOMAINS
    ? (JSON.parse(process.env.SITE_DOMAINS) as SiteDomainLocale[])
    : ((domains as SiteDomainLocale[]) ?? []);

  return configuredDomains.map(normalizeSiteDomain);
}

export function getSiteDomainObject(site: string, locale: string): SiteDomainLocale | null {
  const siteDomains = getConfiguredSiteDomains();
  const lowerLocale = locale.toLowerCase();
  const siteDomain = siteDomains.find(
    (s) =>
      s.siteName === site &&
      (s.defaultLocale.toLowerCase() === lowerLocale ||
        s.locales?.some((l) => l.toLowerCase() === lowerLocale))
  );
  if (!siteDomain) {
    // Fall back to the merged site list (sites.json + domain query) which is the
    // populated, authoritative host→site→locale mapping. The SITE_DOMAINS / SITES
    // environment variables are optional overrides and are not always present, so
    // without this fallback domain resolution would always be null and callers
    // (canonical URL, hreflang, etc.) would incorrectly use PUBLIC_URL.
    const sites: SiteInfo[] = process.env.SITES ? JSON.parse(process.env.SITES) : mergedSites;
    const siteInfo = sites.find(
      (s) =>
        s.name === site &&
        s.language.toLowerCase() === lowerLocale &&
        // Wildcard hosts (e.g. `*`) are catch-all routing entries, not real
        // canonical domains, so never use them to build absolute URLs.
        !s.hostName.includes('*')
    );
    if (!siteInfo) {
      return null;
    }
    return {
      siteName: siteInfo.name,
      domain: siteInfo.hostName,
      previewDomain: siteInfo.hostName,
      defaultLocale: siteInfo.language.toLowerCase(),
      locales: [siteInfo.language.toLowerCase()] as string[],
    };
  }
  return siteDomain;
}

export function getSiteDefaultLocale(site: string, fallbackLocale?: string): string {
  const siteDomains = getConfiguredSiteDomains().filter(
    (siteDomain) => siteDomain.siteName === site && !!siteDomain.defaultLocale
  );

  const configuredDefaultLocale =
    siteDomains.find((siteDomain) => /^[a-z]{2}-[a-z]{2}$/i.test(siteDomain.defaultLocale))
      ?.defaultLocale || siteDomains[0]?.defaultLocale;

  if (configuredDefaultLocale) {
    return configuredDefaultLocale;
  }

  const exactMergedSiteLocale = mergedSites.find(
    (siteInfo) =>
      siteInfo.name === site &&
      !!siteInfo.language &&
      !siteInfo.hostName.includes('*') &&
      /^[a-z]{2}-[a-z]{2}$/i.test(siteInfo.language)
  )?.language;

  if (exactMergedSiteLocale) {
    return exactMergedSiteLocale;
  }

  return mergedSites.find((siteInfo) => siteInfo.name === site)?.language || fallbackLocale || '';
}

export function getCanonicalSiteLocale(
  site: string,
  locale: string | undefined,
  fallbackLocale?: string
): string {
  const normalizedLocale = locale?.trim().toLowerCase();
  const normalizedFallback = fallbackLocale?.trim().toLowerCase();
  const siteDomain = normalizedLocale ? getSiteDomainObject(site, normalizedLocale) : null;
  const localeCandidates = [siteDomain?.defaultLocale, ...(siteDomain?.locales ?? [])].filter(
    (value): value is string => !!value
  );

  const exactLocale = normalizedLocale
    ? localeCandidates.find((value) => value.toLowerCase() === normalizedLocale)
    : undefined;
  if (exactLocale) {
    return exactLocale;
  }

  const siteInfo = normalizedLocale
    ? mergedSites.find(
        (value) => value.name === site && value.language.toLowerCase() === normalizedLocale
      )
    : undefined;
  if (siteInfo?.language) {
    return siteInfo.language;
  }

  const exactFallback = normalizedFallback
    ? localeCandidates.find((value) => value.toLowerCase() === normalizedFallback)
    : undefined;
  if (exactFallback) {
    return exactFallback;
  }

  return getSiteDefaultLocale(site, fallbackLocale || locale || '');
}

export function getLocaleUrl(
  site: string,
  locale: string,
  path: string,
  noDomain: boolean = false
) {
  const siteDomain = getSiteDomainObject(site, locale);
  const domain = siteDomain?.domain ?? process.env.PUBLIC_URL;
  if (!domain) {
    throw new Error(`Domain not found for site ${site} and locale ${locale}`);
  }
  const qualifiedDomain = domain.startsWith('http') ? domain : `https://${domain}`;

  // Remove the locale from the path if it exists so we don't end up with a double locale in the path
  const pathWithoutLocale = path
    .toLocaleLowerCase()
    .replace(LEADING_DUPLICATED_LOCALE_PREFIX_PATTERN, '');

  const basePath =
    !siteDomain?.defaultLocale || siteDomain?.defaultLocale.toLowerCase() === locale.toLowerCase()
      ? pathWithoutLocale
      : `/${locale.toLowerCase()}${pathWithoutLocale}`;
  const domainQualifiedUrl = new URL(basePath, qualifiedDomain);

  if (noDomain) {
    return domainQualifiedUrl.pathname + domainQualifiedUrl.search + domainQualifiedUrl.hash;
  }
  return domainQualifiedUrl.toString();
}

/**
 * Resolves the fully-qualified base URL (origin) for a given site and locale using the
 * per-site/per-locale domain mapping in SITE_DOMAINS. Falls back to PUBLIC_URL when no
 * site-specific domain is configured (e.g. single-site or local development).
 */
export function getSiteBaseUrl(site: string, locale: string): string {
  const siteDomain = getSiteDomainObject(site, locale);
  const domain = siteDomain?.domain ?? process.env.PUBLIC_URL ?? '';
  if (!domain) {
    return '';
  }
  return domain.startsWith('http') ? domain : `https://${domain}`;
}
